import { useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Loader2, PlugZap, LogOut, RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Pill } from "@/components/projects/project-card-shell";
import { useAdtLogonFlow } from "@/hooks/useAdtLogonFlow";
import {
  useAdtStatus,
  useDisconnectAdt,
  useAdtKeepalive,
} from "@/hooks/useApi";
// O limiar de aviso mora num lugar só: este card avisava a 5 min e a strip do board
// nasceria com 10, e o mesmo dev olha as duas telas na mesma sessão.
import { ADT_EXTENSION_STORE_URL } from "@/lib/adt-extension";
import { SESSION_WARN_MIN, minutesUntil } from "@/lib/code-companion-session";

/** Card pessoal da sessão ADT do dev (S-048), por usuário e por projeto.
 *
 * Separado da conexão-alvo compartilhada (GROW Connection): cada dev conecta a
 * própria identidade SAP e vê só a própria sessão. Enquanto o card está aberto e
 * a sessão viva, um keepalive periódico estende a validade; expirou → reconectar. */
const KEEPALIVE_MS = 5 * 60_000;

export function AdtSessionPanel({ projectId }: { projectId: string }) {
  const { t } = useTranslation();
  const { data } = useAdtStatus(projectId);
  const disconnect = useDisconnectAdt(projectId);
  const keepalive = useAdtKeepalive(projectId);

  const status = data?.status ?? "disconnected";
  const connected = status === "connected";
  const mins = minutesUntil(data?.expires_at);

  // Keepalive enquanto o card está montado e a sessão está viva.
  //
  // O mutate vai por ref de propósito. O objeto do useMutation é novo a cada render
  // e o useAdtStatus re-renderiza este card a cada 60s: com ele na lista de
  // dependências, o efeito reexecutava, limpava o intervalo de 5 min e começava
  // outro — que nunca chegava a disparar. Na prática o keepalive não existia e a
  // sessão morria a cada 30 min mesmo com o card aberto.
  const keepaliveRef = useRef(keepalive.mutate);
  keepaliveRef.current = keepalive.mutate;
  useEffect(() => {
    if (!connected) return;
    const id = setInterval(() => keepaliveRef.current(), KEEPALIVE_MS);
    return () => clearInterval(id);
  }, [connected]);

  // #593: o par pedir-e-abrir e a sondagem saem daqui para o hook.
  //
  // 🔶 Política `on-focus`, e agora declarada em vez de implícita: instalar É sair para
  // outra aba e voltar, e sem re-checar o aviso continuaria dizendo "ausente" justo
  // depois de a pessoa ter feito o que pedimos. A faixa usa `once` de propósito, porque
  // não é tela de onboarding, e essa diferença antes vivia só num comentário de cada
  // lado.
  //
  // 🔶 Avisa e não bloqueia: a detecção pode dar falso negativo (navegador que não é
  // Chromium, ID novo depois da store, política do cliente), e travar o botão por isso
  // seria pior que deixar tentar. Ver S-070.
  const logonFlow = useAdtLogonFlow(projectId, { enabled: !connected, watch: "on-focus" });
  const { needsExtension, extension } = logonFlow;
  const handleConnect = logonFlow.start;
  const expiring = connected && mins !== null && mins <= SESSION_WARN_MIN;
  const label = expiring
    ? t("Expiring")
    : connected
      ? t("Connected")
      : status === "expired"
        ? t("Expired")
        : t("Disconnected");

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h4 className="text-sm font-semibold text-[#08254c]">
            {t("My ADT session")}
          </h4>
          <p className="text-xs text-muted-foreground">
            {t(
              "Your personal write identity in this project's GROW system. Private to you; each developer connects their own.",
            )}
          </p>
        </div>
        <Pill tone={connected && !expiring ? "ok" : "gold"}>{label}</Pill>
      </div>

      {connected && (
        <div className="text-sm text-muted-foreground space-y-0.5">
          <div>
            {t("Connected as")}{" "}
            <span className="font-mono text-[#08254c]">
              {data?.sap_responsible || "—"}
            </span>
          </div>
          {mins !== null && (
            <div
              className={expiring ? "text-amber-700 font-medium" : undefined}
            >
              {t("Expires in ~{{mins}} min", { mins })}
            </div>
          )}
        </div>
      )}
      {status === "expired" && (
        <p className="text-sm text-amber-700">
          {t("Your ADT session expired. Reconnect to keep writing to GROW.")}
        </p>
      )}
      {needsExtension && !connected && extension === null && (
        // O SAP só devolve o ticket de login para `localhost`, e neste ambiente o
        // myGROW não está lá. Sem a extensão o dev cai numa página de erro do browser
        // sem entender por quê, então o recado vem ANTES do clique. Ver S-070.
        <div className="space-y-2">
          <p data-testid="adt-ext-ausente" className="text-xs text-amber-700">
            {t(
              "Connecting from here needs the myGROW ADT Connect browser extension. Without it the SAP login has nowhere to return to.",
            )}
          </p>
          <a
            data-testid="adt-ext-instalar"
            href={ADT_EXTENSION_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block text-xs font-medium text-[#08254c] underline hover:no-underline"
          >
            {t("Install the extension")}
          </a>
        </div>
      )}
      {needsExtension && !connected && extension?.version && (
        <p data-testid="adt-ext-ok" className="text-xs text-muted-foreground">
          {t("Extension detected (v{{v}})", { v: extension.version })}
        </p>
      )}

      <div className="flex items-center gap-3">
        <Button onClick={handleConnect} disabled={logonFlow.isPending}>
          {logonFlow.isPending ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : connected ? (
            <RefreshCw className="w-4 h-4 mr-2" />
          ) : (
            <PlugZap className="w-4 h-4 mr-2" />
          )}
          {connected ? t("Reconnect") : t("Connect ADT")}
        </Button>
        {connected && (
          <Button
            variant="outline"
            onClick={() => disconnect.mutate()}
            disabled={disconnect.isPending}
          >
            <LogOut className="w-4 h-4 mr-2" /> {t("Sign out")}
          </Button>
        )}
      </div>
      {/* #593, achado na review: o hook captura a recusa do logon e alguém precisa
          renderizá-la. Sem isto um 409 ("já há uma sessão em curso") para o spinner e
          não deixa nada na tela, que é pior do que antes da extração: o `mutateAsync`
          não aguardado ao menos aparecia no devtools. */}
      {logonFlow.error !== null && (
        <p data-testid="adt-logon-error" className="mt-2 text-sm text-red-700">
          {logonFlow.error || t("Could not start the ADT logon.")}
        </p>
      )}
    </div>
  );
}
